import { useEffect, useRef, useState, useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useGetMe } from '@shared/services/get-me';
import { canManageKnowledge } from '../helpers/can-manage-knowledge';
import {
  ASSISTANT_PANEL_DEFAULT_WIDTH,
  ASSISTANT_PANEL_MIN_WIDTH,
  clampAssistantPanelWidth,
  persistAssistantPanelWidth,
  readStoredAssistantPanelWidth,
} from '../helpers/panel-width';
import { useAssistantStore } from '../store/assistant-store';
import { detectRecordContext } from '../hooks/use-record-context';
import { useOpenRecord } from '../hooks/use-open-record';
import type { AgentState } from '../types/agent-state.types';
import type { TablePresentation } from '../types/presentation.types';
import type { TiaReaction } from './tia/tia-avatar';
import { CompanionHeader } from './companion-header';
import { MessageThread } from './message-thread';
import { ConversationList } from './conversation-list';
import { TiaKnowledgeBasePanel } from './tia-knowledge-base-panel';
import { AssistantResizeHandle } from './assistant-resize-handle';
import { AgentTableModal } from './presentation/agent-table-modal';
import { useAssistantScope } from '../hooks/use-assistant-scope';

export function AssistantPanel() {
  const scope = useAssistantScope();
  // The store remembers its own scope, so a user change is caught even when
  // this panel was unmounted in between (logout -> login page -> login).
  useLayoutEffect(() => {
    useAssistantStore.getState().bindScope(scope);
  }, [scope]);
  const isOpen = useAssistantStore((s) => s.isOpen);
  const close = useAssistantStore((s) => s.close);
  const setRecordContext = useAssistantStore((s) => s.setRecordContext);
  const setView = useAssistantStore((s) => s.setView);
  const view = useAssistantStore((s) => s.view);
  const startConversation = useAssistantStore((s) => s.startConversation);
  const activeConversationId = useAssistantStore((s) => s.activeConversationId);
  const openRecord = useOpenRecord();
  const asideRef = useRef<HTMLElement>(null);

  // The live agent state for the header avatar. The chat hook (and thus the
  // run signal) lives inside MessageThread — keyed per conversation — so the
  // thread derives the state and lifts it here; 'idle' outside a thread.
  const [agentState, setAgentState] = useState<AgentState>('idle');

  // FR-15 micro-reaction: set by the confirmation card (via the thread),
  // played once by the header avatar, cleared when its cycle ends.
  const [reaction, setReaction] = useState<TiaReaction | null>(null);

  // FR-11 tool narration, lifted from the thread like the agent state. The
  // header only prefers it while `searching` — every other state keeps its
  // own label (confirm/error/done must never be masked by a stale phrase).
  const [narration, setNarration] = useState<string | null>(null);

  // Table presentation opens in an overlay dialog (Figma View chip).
  const [openTable, setOpenTable] = useState<TablePresentation | null>(null);

  // Knowledge-base management gate: `members/me/` is the CRM session the app
  // already caches (staleTime Infinity). The helper reads the server-derived
  // `can_manage_knowledge` flag — the same permission the Django backend
  // enforces on every /knowledge endpoint — so there is no separate
  // role-name rule to drift out of sync with it. The server enforces auth +
  // org scoping on every endpoint regardless; this only decides whether the
  // Knowledge tab and manage controls show.
  const { data: me } = useGetMe();
  const canManage = canManageKnowledge(me);

  const [panelWidth, setPanelWidth] = useState(readStoredAssistantPanelWidth);

  const setWidth = (next: number) => {
    const clamped = clampAssistantPanelWidth(next);
    setPanelWidth(clamped);
    persistAssistantPanelWidth(clamped);
  };

  // Re-clamp after code/HMR updates so a stale in-memory width below the
  // new floor cannot stick until a full remount.
  useEffect(() => {
    setPanelWidth((current) => {
      const next = clampAssistantPanelWidth(current);
      if (next !== current) persistAssistantPanelWidth(next);
      return next;
    });
  }, []);

  // Key the sync on `pathname` (a stable primitive), NOT on the freshly-built detected object —
  // otherwise the effect runs every render -> setRecordContext -> re-render -> infinite loop.
  const { pathname } = useLocation();
  useEffect(() => {
    setRecordContext(detectRecordContext(pathname));
  }, [pathname, setRecordContext]);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      // Table dialog owns Escape while open (Radix closes via onOpenChange).
      if (openTable) return;
      event.preventDefault();
      close();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, close, openTable]);

  // Drop the modal when leaving the thread or switching conversations.
  useEffect(() => {
    setOpenTable(null);
  }, [activeConversationId, view]);

  useEffect(() => {
    if (!isOpen) setOpenTable(null);
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <aside
      ref={asideRef}
      data-slot='assistant-panel'
      style={{ width: panelWidth, minWidth: ASSISTANT_PANEL_MIN_WIDTH }}
      className='scrollbar-none-deep relative flex h-full min-h-0 shrink-0 flex-col bg-primary-50 p-4'
    >
      <h2 className='sr-only'>TIA assistant</h2>
      <AssistantResizeHandle
        width={panelWidth}
        onWidthChange={setWidth}
        onReset={() => setWidth(ASSISTANT_PANEL_DEFAULT_WIDTH)}
      />
      {openTable && (
        <AgentTableModal
          open
          onClose={() => setOpenTable(null)}
          block={openTable}
          onOpenRecord={openRecord}
          asideRef={asideRef}
        />
      )}
      <div className='flex min-h-0 h-full w-full flex-col overflow-hidden rounded-2xl border border-primary-200 bg-white shadow-[0px_4px_75px_0px_rgba(0,0,0,0.09)]'>
        {view === 'knowledge' ? (
          <TiaKnowledgeBasePanel
            onBack={() => setView('list')}
            onClose={close}
          />
        ) : (
          <>
            <CompanionHeader
              state={agentState}
              narration={agentState === 'searching' ? narration : null}
              reaction={reaction}
              onReactionEnd={() => setReaction(null)}
              activeView={view}
              onViewChange={setView}
              onNewChat={() => startConversation(null)}
              onClose={close}
            />
            <div
              className='flex-1 overflow-hidden'
              data-slot='assistant-body'
            >
              {view === 'list' ? (
                <ConversationList canManageKnowledge={canManage} />
              ) : (
                /* Keyed per conversation: the chat SDK client resets with the thread. */
                <MessageThread
                  key={`${scope}:${activeConversationId ?? 'new'}`}
                  onAgentStateChange={setAgentState}
                  onConfirmationReact={setReaction}
                  onNarrationChange={setNarration}
                  onOpenTable={setOpenTable}
                />
              )}
            </div>
          </>
        )}
      </div>
    </aside>
  );
}
